import React from "react";
import _ from "lodash";
import {
  ClockTimerPendingFilled,
  TickCircleFilled,
  WarningCircleFilled,
} from "@toddle-edu/ds-icons";
import { compose, graphql } from "lib/apolloClient";
import { connect } from "react-redux";
import { getWorkflowProgressDataQuery } from "Workflows/modules/WorkflowsQueries";
import withLoader from "UIComponents/GqlWithLoader";
import withPusherBind from "UIComponents/PusherConsumerHoc";
import FeedListv2 from "AppComponents/FeedListv2";
import classes from "./Step2.scss";

const columnList = [
  {
    locale: "common:updates",
    id: "updates",
    type: "TEXTCOLUMN",
    style: {
      width: "375px",
    },
  },
  {
    locale: "integration:sync_status",
    id: "status",
    type: "CUSTOM",
  },
];

const getStatusSummary = ({ stepType, stepCountByType, groupedSteps }) => {
  const expectedCount = stepCountByType[stepType];
  const currentSteps = groupedSteps[stepType] || [];

  const successCount = _.filter(currentSteps, { status: "COMPLETED" }).length;
  const failedCount = _.filter(currentSteps, { status: "FAILED" }).length;
  const noChangeCount = _.filter(currentSteps, { status: "NO_CHANGE" }).length;

  if (noChangeCount > 0) {
    return {
      status: "NO_CHANGE",
      successCount: 0,
      failedCount: 0,
      totalCount: 0,
    };
  }

  if (_.isNil(expectedCount)) {
    return {
      status: "PROCESSING",
      successCount: 0,
      failedCount: 0,
      totalCount: 0,
    };
  }

  if (expectedCount === 0) {
    return {
      status: "COMPLETED",
      successCount: 0,
      failedCount: 0,
      totalCount: 0,
    };
  }

  if (currentSteps.length < expectedCount) {
    return {
      status: "PROCESSING",
      successCount,
      failedCount,
      totalCount: expectedCount,
    };
  }

  if (failedCount > 0) {
    return {
      status: "FAILED",
      successCount,
      failedCount,
      totalCount: expectedCount,
    };
  }

  if (successCount === expectedCount) {
    return {
      status: "COMPLETED",
      successCount,
      failedCount,
      totalCount: expectedCount,
    };
  }

  return {
    status: "PROCESSING",
    successCount,
    failedCount,
    totalCount: expectedCount,
  };
};

const getJobData = ({ sortedNodes, stepCountByType, groupedSteps, t }) => {
  const data = _.map(sortedNodes, (node, index) => {
    const { stepType, localeKey = "" } = node;
    const summary = getStatusSummary({
      stepType,
      stepCountByType,
      groupedSteps,
    });

    return {
      id: index + 1,
      status: { summary },
      updates: t(`workflows:${localeKey}`),
    };
  });

  return data;
};

const getStatusConfigs = ({
  status,
  successCount,
  failedCount,
  totalCount,
  t,
}) => {
  if (status === "PROCESSING") {
    return {
      icon: <ClockTimerPendingFilled size="xx-small" variant="warning" />,
      label: t("project:ongoing"),
    };
  }

  if (status === "NO_CHANGE") {
    return {
      icon: <WarningCircleFilled size="xx-small" variant="warning" />,
      label: t("progressReport:comment_bank_error_no_changes_header"),
    };
  }

  if (totalCount === 0) {
    return {
      icon: <TickCircleFilled size="xx-small" variant="success" />,
      label: t("common:skipped"),
    };
  }

  if (failedCount === totalCount) {
    return {
      icon: <WarningCircleFilled size="xx-small" variant="critical" />,
      label: t("common:failed_with_count", { count: failedCount }),
    };
  }

  return {
    icon: (
      <TickCircleFilled
        size="xx-small"
        variant={successCount < totalCount ? "warning" : "success"}
      />
    ),
    label: t("workflows:m_of_n_successful", {
      successCount,
      totalCount,
    }),
  };
};

const FeedList = props => {
  const { nodes, steps, stepCountByType, t } = props;

  // Process the subtasks to get statistics
  const groupedSteps = _.groupBy(steps, "stepType");

  const sortedNodes = _.sortBy(nodes, "stepOrder");

  const getCustomFeedComponent = ({ column, columnData, t }) => {
    switch (column.id) {
      case "status": {
        const { summary = {} } = columnData;
        const { icon: Icon, label } = getStatusConfigs({
          ...summary,
          t,
        });

        return (
          <span className={classes.statusButtonWrapper}>
            {Icon}
            {label}
          </span>
        );
      }
    }
  };

  return (
    <div className={classes.feedContainer}>
      <FeedListv2
        columnList={columnList}
        feedData={getJobData({
          sortedNodes,
          stepCountByType,
          groupedSteps,
          t,
        })}
        getCustomComponent={getCustomFeedComponent}
        showBorder={true}
        headerTheme={"subtle"}
        enableStickyHeader={true}
        dsVersion="2.0"
      />
    </div>
  );
};

const mapStateToProps = (state, { subTaskId }) => ({
  isData: true,
  isLoading: false,
  variables: { subTaskId },
});

export default compose(
  connect(mapStateToProps, null),
  graphql(getWorkflowProgressDataQuery, {
    name: "getWorkflowProgressData",
    options: ({ variables }) => ({
      fetchPolicy: "cache-and-network",
      variables,
    }),
    props: ({ getWorkflowProgressData, ownProps: { isData, isLoading } }) => {
      const subTasksNode = _.get(getWorkflowProgressData, "integration", {});
      const {
        nodes = [],
        stepCountByType = {},
        steps = [],
        subTaskData = {},
      } = _.get(subTasksNode, "fetchWorkflowProgressData", {});

      return {
        getWorkflowProgressData,
        nodes,
        stepCountByType,
        steps,
        subTaskData,
        isData: !_.isEmpty(subTasksNode) && isData,
        isLoading:
          getWorkflowProgressData["networkStatus"] == 1 ||
          getWorkflowProgressData["networkStatus"] == 2 ||
          isLoading,

        onPusherEventTriggered: () => {
          getWorkflowProgressData.refetch();
        },
      };
    },
  }),
  withPusherBind(["workflowProgressUpdate"]),
  withLoader
)(FeedList);
